import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { getSessionUser } from "../services/sessionStorage";
import * as authService from "../services/authService";

function hasRole(role: string) {
  const u = getSessionUser();
  return !!u?.roles?.includes(role);
}

function HomeLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionUser = getSessionUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const puedeVerUsuarios = hasRole("SYSTEM_ADMIN");

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  function cerrarSesion() {
    authService.logout();
    navigate("/");
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? "flex items-center gap-3 px-3 py-3 rounded-lg bg-primary/20 text-white group transition-colors"
      : "flex items-center gap-3 px-3 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 group transition-colors";

  const configClass =
    "flex w-full items-center gap-3 px-3 py-3 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 group transition-colors";

  const navItems = (
    <>
      {puedeVerUsuarios && (
        <NavLink to="usuarios" className={navLinkClass}>
          <span className="material-symbols-outlined text-gold-accent group-hover:text-white transition-colors">
            group
          </span>
          <span className="text-sm font-medium">Usuarios</span>
        </NavLink>
      )}

      <NavLink to="eventos" className={navLinkClass}>
        <span className="material-symbols-outlined group-hover:text-gold-accent transition-colors">
          calendar_month
        </span>
        <span className="text-sm font-medium">Eventos</span>
      </NavLink>

      <NavLink to="rsvp" className={navLinkClass}>
        <span className="material-symbols-outlined group-hover:text-gold-accent transition-colors">
          how_to_reg
        </span>
        <span className="text-sm font-medium">RSVP</span>
      </NavLink>

      <button className={configClass} type="button">
        <span className="material-symbols-outlined group-hover:text-gold-accent transition-colors">
          settings
        </span>
        <span className="text-sm font-medium">Configuración</span>
      </button>
    </>
  );

  const profileBlock = (
    <>
      <div className="flex items-center gap-3">
        <div
          className="size-10 rounded-full bg-cover bg-center border border-white/10"
          style={{
            backgroundImage:
              "url('https://lh3.googleusercontent.com/aida-public/AB6AXuBZsVxuAaQHABYZmaMDgrUYfCSMCWnlBONlwJB2Hw-6QBbxeJjduUFWBLqQ6kyF2npyNLGlCND75Kj8azKSvjik3Pk1-xjo0fki_NaCm3kVNaKkMDHED6d33Lfe08Vor0jqnT3ARWk7qCFtpun3kXyQdarPdFRm95QJlH30CEXPXJByOWdWQIVcfWEd2CqNiNXTo-efoZLbK7ccQisrE0pcwkdpAbZUi4I70CAZ6qRb2wt8fBLO3wv7BS9--eXVEY_Ewvf8ZUluEK87')",
          }}
        ></div>

        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium text-white truncate">
            {sessionUser?.nombre ?? "Usuario"}
          </span>
          <span className="text-xs text-slate-400 truncate">
            {sessionUser?.email ?? ""}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={cerrarSesion}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
      >
        <span className="material-symbols-outlined text-[18px]">logout</span>
        Cerrar sesión
      </button>
    </>
  );

  return (
    <div className="app-shell bg-background-light dark:bg-background-dark text-slate-900 dark:text-white font-display antialiased selection:bg-gold-accent selection:text-black">
      <div className="relative flex h-full w-full flex-row overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r border-white/5 bg-surface-darker hidden lg:flex flex-col">
          <div className="flex h-16 items-center px-6 border-b border-white/5">
            <div className="size-8 rounded-full bg-gradient-to-tr from-gold-accent to-primary mr-3"></div>
            <h1 className="text-lg font-serif font-semibold tracking-wide text-white">
              Wedding Planner
            </h1>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
            {navItems}
          </nav>

          {/* Perfil + Logout */}
          <div className="p-4 border-t border-white/5">
            {profileBlock}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col min-w-0 bg-background-light dark:bg-background-dark overflow-y-auto">
          {/* Mobile header */}
          <header className="mobile-safe-header lg:hidden sticky top-0 z-40 flex items-center justify-between px-5 pb-4 bg-surface-darker border-b border-white/5 shadow-lg shadow-black/10">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-full bg-gradient-to-tr from-gold-accent to-primary shrink-0"></div>
              <div className="min-w-0">
                <h1 className="text-lg font-serif font-semibold tracking-wide text-white truncate">
                  Wedding Planner
                </h1>
                <p className="text-xs text-slate-400 truncate">
                  {sessionUser?.nombre ?? "Panel administrativo"}
                </p>
              </div>
            </div>
            <button
              className="size-12 rounded-xl border border-white/10 bg-white/5 text-white flex items-center justify-center active:scale-95 transition"
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menu"
              aria-expanded={mobileMenuOpen}
            >
              <span className="material-symbols-outlined text-[28px]">menu</span>
            </button>
          </header>

          <Outlet />
        </main>

        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <button
              type="button"
              className="absolute inset-0 bg-black/60"
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Cerrar menu"
            />

            <aside className="mobile-safe-drawer absolute left-0 top-0 h-full w-[min(88vw,22rem)] bg-surface-darker border-r border-white/10 shadow-2xl flex flex-col">
              <div className="px-5 pb-5 border-b border-white/5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="size-10 rounded-full bg-gradient-to-tr from-gold-accent to-primary shrink-0"></div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-serif font-semibold tracking-wide text-white truncate">
                      Wedding Planner
                    </h2>
                    <p className="text-xs text-slate-400 truncate">Menu principal</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="size-11 rounded-xl border border-white/10 bg-white/5 text-white flex items-center justify-center"
                  aria-label="Cerrar menu"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto px-4 py-5 space-y-1">
                {navItems}
              </nav>

              <div className="p-4 border-t border-white/5">
                {profileBlock}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomeLayout;
